import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 *
 * You can be released from the terms, and requirements of the Academic Public
 * License by purchasing a commercial license.
 * Purchase of a commercial license is mandatory for any use of the
 * LMX Software in commercial settings.
 */
// END COPYRIGHT
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import ScoreboardRow from "./ScoreboardRow";
import { HelpIcon } from "../styles/HelpIcon";
import { LightTooltip } from "../styles/Tooltips";
import { landingPalette } from "../theme";
import { IdeaSource } from "../types";
// #region: Styled Components
const Card = styled(Paper)(({ theme }) => ({
    borderRadius: 8,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
}));
const EmptyState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3, 2),
    textAlign: "center",
}));
const FrontierPill = styled(Box)(({ theme }) => ({
    display: "inline-block",
    padding: "5px 10px",
    borderRadius: 999,
    background: theme.palette.action.selected,
    color: theme.palette.text.primary,
    fontSize: "0.95rem",
    whiteSpace: "nowrap",
    marginLeft: theme.spacing(1),
}));
// A link in prose, not a control: it reads as part of the sentence but opens the ideation panel.
const PromptButton = styled("button")(({ theme }) => ({
    padding: 0,
    border: "none",
    background: "none",
    font: "inherit",
    color: landingPalette.link,
    cursor: "pointer",
    textAlign: "left",
    textDecoration: "none",
    "&:hover, &:focus-visible": {
        color: landingPalette.linkHover,
        textDecoration: "underline",
        textUnderlineOffset: "2px",
    },
    "&:disabled": {
        color: theme.palette.text.disabled,
        cursor: "default",
        textDecoration: "none",
    },
}));
const ScoreboardContainer = styled(Box)(() => ({
    marginBottom: 24,
}));
const Subtitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
}));
// The lead line sits beside the pill, so it tracks the same size to keep the footer level.
const SummaryText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: "0.95rem",
}));
const TitleHeading = styled("h2")(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: 600,
    margin: 0,
    fontSize: "1.25rem",
}));
const TitleRow = styled(Box)(() => ({
    display: "flex",
    alignItems: "center",
}));
// #endregion: Types
const Scoreboard = ({ data, onRequestIdea, onGenerateIdea, isRequestInProgress = false }) => {
    const players = data?.Player || [];
    const ideasGenerated = data?.["Ideas Generated"] || [];
    const optimalSet = data?.["Pareto Ideas"] || [];
    const optimalSetRate = data?.["Pareto Rate"] || [];
    const humanIdx = players.indexOf(IdeaSource.HUMAN);
    const machineIdx = players.indexOf(IdeaSource.AI);
    const humanPct = (optimalSetRate[humanIdx] || 0) * 100;
    const machinePct = (optimalSetRate[machineIdx] || 0) * 100;
    const humanCount = optimalSet[humanIdx] || 0;
    const humanTotal = ideasGenerated[humanIdx] || 0;
    const machineCount = optimalSet[machineIdx] || 0;
    const machineTotal = ideasGenerated[machineIdx] || 0;
    const frontierTotal = humanCount + machineCount;
    const anyIdeas = humanTotal > 0 || machineTotal > 0;
    const bothSubmitted = humanTotal > 0 && machineTotal > 0;
    let summary;
    if (!bothSubmitted) {
        const missingSource = humanTotal === 0 ? IdeaSource.HUMAN : IdeaSource.AI;
        summary = (_jsx(PromptButton, { type: "button", onClick: () => onRequestIdea(missingSource), children: `Add ${missingSource === IdeaSource.HUMAN ? "a human" : "an AI"} idea to compare` }));
        // Compared as displayed, so two rows showing the same percentage never read as a lead.
    }
    else if (humanPct.toFixed(1) === machinePct.toFixed(1)) {
        summary = _jsx("strong", { children: "Human and AI are tied" });
    }
    else {
        summary = _jsx("strong", { children: `${humanPct > machinePct ? "Human" : "AI"} leads` });
    }
    return (_jsxs(ScoreboardContainer, { "data-testid": "scoreboard-container-div", children: [_jsxs(TitleRow, { children: [_jsx(TitleHeading, { children: "Pareto optimality rate" }), _jsx(LightTooltip, { arrow: true, placement: "top", title: "An idea is optimal when no other idea scores higher on both objectives at the same time; " +
                            "those ideas form the Pareto front. This is the percent of each side's ideas that qualify. " +
                            "A lead is only claimed once both sides have submitted an idea.", children: _jsx(HelpIcon, { "aria-label": "Scoreboard help" }) })] }), _jsx(Subtitle, { variant: "body2", children: "Percent of each side\u2019s ideas that no other idea beats on both objectives at the same time." }), _jsx(Card, { children: !anyIdeas ? (_jsxs(EmptyState, { children: [_jsx(Typography, { fontWeight: 600, children: "No ideas yet" }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [_jsx(PromptButton, { type: "button", disabled: isRequestInProgress, onClick: onGenerateIdea ?? (() => onRequestIdea(IdeaSource.AI)), children: "Generate an idea" }), " or ", _jsx(PromptButton, { type: "button", onClick: () => onRequestIdea(IdeaSource.HUMAN), children: "add your own" }), " to see rates here."] })] })) : (_jsxs(_Fragment, { children: [_jsx(ScoreboardRow, { source: IdeaSource.HUMAN, pct: humanPct, count: humanCount, total: humanTotal }), _jsx(ScoreboardRow, { source: IdeaSource.AI, pct: machinePct, count: machineCount, total: machineTotal }), _jsxs(Box, { sx: {
                                mt: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                            }, children: [_jsx(SummaryText, { variant: "body2", children: summary }), _jsx(Box, { children: _jsxs(FrontierPill, { children: ["Frontier: ", frontierTotal, " idea", frontierTotal === 1 ? "" : "s"] }) })] })] })) })] }));
};
export default Scoreboard;
